import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Chip,
  Avatar,
  Paper,
  Divider,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Fade,
  Badge,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as CopyIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  Lightbulb as LightbulbIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { getDateFnsLocale } from "../../utils/date-utils";
import { useTranslation } from "react-i18next";

import aiSupportApi from "../../services/api/ai-support.api";
import { useVoiceRecording } from "../../hooks/useVoiceRecording";
import { logger } from "../../utils/logger";
import { AiConsentGate } from "../ai/AiConsentGate";
import { useAiConsent } from "../../hooks/useAiConsent";
import { SUPPORT_EMAIL } from "../../constants/support";

/**
 * Ответ POST /ai-support/sessions/:id/messages — ищем aiMessage.content
 * (Nest + обёртки data). `confidence` возвращается только если он реально
 * пришёл: прежний код подставлял 0.8 вместо отсутствующего значения, и
 * интерфейс печатал «Уверенность: 80 %» под ответом, у которого её не было.
 */
function extractAiReplyFromMessagesResponse(response: unknown): {
  content: string;
  confidence?: number;
} {
  const findAiMessage = (
    obj: unknown,
    depth = 0,
  ): { content?: string; confidence?: number } | undefined => {
    if (depth > 4 || !obj || typeof obj !== "object") return undefined;
    const o = obj as Record<string, unknown>;
    const am = o["aiMessage"];
    if (am && typeof am === "object" && "content" in (am as object)) {
      return am as { content?: string; confidence?: number };
    }
    if (o["data"] !== undefined) {
      return findAiMessage(o["data"], depth + 1);
    }
    return undefined;
  };

  const aiMessage = findAiMessage(response);
  if (
    aiMessage &&
    typeof aiMessage.content === "string" &&
    aiMessage.content.trim()
  ) {
    return {
      content: aiMessage.content,
      ...(typeof aiMessage.confidence === "number" && {
        confidence: aiMessage.confidence,
      }),
    };
  }

  if (response && typeof response === "object") {
    const inner = (response as Record<string, unknown>)["data"] ?? response;
    const block = inner as Record<string, unknown>;
    if (typeof block["content"] === "string") {
      return {
        content: block["content"],
        ...(typeof block["confidence"] === "number" && {
          confidence: block["confidence"],
        }),
      };
    }
    if (typeof block["message"] === "string") {
      return {
        content: block["message"],
        ...(typeof block["confidence"] === "number" && {
          confidence: block["confidence"],
        }),
      };
    }
  }
  return { content: "" };
}

// Типы сообщений
interface Message {
  id: string;
  content: string;
  sender: "user" | "ai" | "operator";
  timestamp: Date;
  type: "text" | "action" | "suggestion" | "escalation";
  metadata?: {
    confidence?: number;
    suggestedActions?: string[];
    escalationReason?: string;
    operatorInfo?: {
      name: string;
      avatar?: string;
      role: string;
    };
  };
}

// Быстрые ответы
interface QuickReply {
  id: string;
  text: string;
  category: "events" | "account" | "billing" | "technical";
  icon?: string;
}

/**
 * Виджет — ЕДИНСТВЕННЫЙ владелец ленты сообщений на экране. Всё, что обязано
 * быть общим с плавающим лаунчером (сессия, счётчик попыток AI, запрос
 * эскалации), приходит сверху из `useSupport`. До 31.08.2026 виджет держал
 * СВОЮ `sessionId` рядом с сессией хука: открытие чата создавало в БД пустую
 * сессию, лаунчер эскалировал именно её, а ворота ТЗ §1.1 («оператор после трёх
 * ответов AI») не открывались никогда — счётчик рос в хуке, а сообщения ходили
 * здесь (F-SUP-2).
 */
interface SupportWidgetProps {
  open: boolean;
  onClose: () => void;
  /** Текст, с которым чат открыли снаружи (быстрое действие навигации). */
  initialMessage?: string;
  /** Сообщить координатору, что начальный текст ушёл — чтобы не уйти дважды. */
  onInitialMessageSent?: () => void;
  /** Идентификатор ОБЩЕЙ сессии; `null` — сессии ещё нет. */
  sessionId: string | null;
  /** Создаёт общую сессию по требованию. `null` — бэкенд отказал. */
  ensureSession: () => Promise<string | null>;
  /** Один ответ AI = одна попытка линии L1 (ТЗ §1.1). */
  onAiReply: () => void;
  /** Ворота L2: кнопка оператора появляется только после трёх ответов AI. */
  canRequestOperator: boolean;
  /** Лаунчер попросил оператора — эскалацию выполняем здесь, в этой ленте. */
  escalationRequested?: boolean;
  onEscalationHandled?: () => void;
}

// Локализация будет получена через useTranslation

const SupportWidget: React.FC<SupportWidgetProps> = ({
  open,
  onClose,
  initialMessage,
  onInitialMessageSent,
  sessionId,
  ensureSession,
  onAiReply,
  canRequestOperator,
  escalationRequested = false,
  onEscalationHandled,
}) => {
  const { t } = useTranslation("support");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ADR-008 §5 / W5-S2: AI-чат под согласием `aiProcessing` (fail-closed).
  // Гейтит ввод и блокирует AI-вызовы (вкл. авто-отправку initialMessage).
  const { hasAiConsent } = useAiConsent();

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Состояние
  const [currentTab, setCurrentTab] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isConnectedToOperator, setIsConnectedToOperator] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [isVoiceTranscribing, setIsVoiceTranscribing] = useState(false);
  const [voiceSubmitError, setVoiceSubmitError] = useState<string | null>(null);
  const {
    isRecording: isVoiceRecording,
    error: voiceHookError,
    start: startVoiceRecording,
    stop: stopVoiceRecording,
    clearError: clearVoiceError,
  } = useVoiceRecording();

  // Функция для получения иконки по имени
  const getIconComponent = (iconName?: string): React.ReactElement => {
    switch (iconName) {
      case "event":
        return <EventIcon />;
      case "people":
        return <PeopleIcon />;
      case "payment":
        return <PaymentIcon />;
      case "settings":
        return <SettingsIcon />;
      case "build":
        return <BuildIcon />;
      case "lightbulb":
        return <LightbulbIcon />;
      case "help":
        return <HelpIcon />;
      default:
        return <HelpIcon />;
    }
  };

  // Быстрые ответы с локализацией (мемоизировано по языку)
  const quickReplies: QuickReply[] = useMemo(
    () => [
      {
        id: "create-event",
        text: t("quickHelp.createEvent"),
        category: "events",
        icon: "event",
      },
      {
        id: "manage-participants",
        text: t("quickHelp.manageParticipants"),
        category: "events",
        icon: "people",
      },
      {
        id: "payment-issue",
        text: t("quickHelp.paymentIssue"),
        category: "billing",
        icon: "payment",
      },
      {
        id: "account-settings",
        text: t("quickHelp.accountSettings"),
        category: "account",
        icon: "settings",
      },
      {
        id: "technical-problem",
        text: t("quickHelp.technicalProblem"),
        category: "technical",
        icon: "build",
      },
      {
        id: "feature-request",
        text: t("quickHelp.featureRequest"),
        category: "technical",
        icon: "lightbulb",
      },
    ],
    [t],
  );

  // Отправляем отзыв на конкретное сообщение AI
  const handleFeedbackSubmit = useCallback(
    async (
      _messageId: string,
      lastAiMsg: string,
      reason:
        | "irrelevant"
        | "too_generic"
        | "incorrect"
        | "missing_steps"
        | "needs_human",
    ) => {
      setFeedbackFor(null);
      try {
        await aiSupportApi.submitFeedback({
          reason,
          expectedOutcome:
            reason === "needs_human" ? "operator" : "exact_steps",
          ...(sessionId ? { sessionId } : {}),
          lastAiMessage: lastAiMsg,
          consent: true,
        });
      } catch {
        // silent — feedback is best-effort
      }
    },
    [sessionId],
  );

  // Обработка отправки сообщения
  const handleSendMessage = useCallback(
    async (content: string = inputValue) => {
      if (!content.trim()) return;
      // fail-closed: без согласия `aiProcessing` AI-вызов не уходит
      // (в т.ч. авто-отправка initialMessage); гейт покажет запрос согласия.
      if (!hasAiConsent) return;

      const userMessage: Message = {
        id: `user_${Date.now()}`,
        content: content.trim(),
        sender: "user",
        timestamp: new Date(),
        type: "text",
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsTyping(true);

      const pushFailure = (text: string) =>
        setMessages((prev) => [
          ...prev,
          {
            id: `error_${Date.now()}`,
            content: text,
            sender: "ai",
            timestamp: new Date(),
            type: "text",
          },
        ]);

      try {
        const sid = await ensureSession();
        if (!sid) {
          // Сессию создать не удалось — ответа AI не будет. Показываем отказ и
          // канал, который от AI-контура не зависит, а не «ответ» без ответа.
          pushFailure(t("errors.openFailed", { email: SUPPORT_EMAIL }));
          return;
        }

        const data = await aiSupportApi.sendMessage(sid, content.trim());
        const extracted = extractAiReplyFromMessagesResponse(data);
        if (!extracted.content) {
          pushFailure(t("errors.sendFailed", { email: SUPPORT_EMAIL }));
          return;
        }

        const aiResponse: Message = {
          id: `ai_${Date.now()}`,
          content: extracted.content,
          sender: "ai",
          timestamp: new Date(),
          type: "text",
          ...(extracted.confidence != null && {
            metadata: { confidence: extracted.confidence },
          }),
        };
        setMessages((prev) => [...prev, aiResponse]);
        // Ответ AI получен — это одна попытка линии L1. Только отсюда двигаются
        // ворота ТЗ §1.1: сообщения ходят здесь и больше нигде.
        onAiReply();
      } catch (error) {
        logger.error("AI Response error:", error);
        pushFailure(t("errors.sendFailed", { email: SUPPORT_EMAIL }));
      } finally {
        setIsTyping(false);
      }
    },
    [inputValue, ensureSession, onAiReply, t, hasAiConsent],
  );

  const handleVoiceToggle = useCallback(async () => {
    if (isVoiceTranscribing || isTyping) return;
    if (!hasAiConsent) return; // голос → speechToText (AI) тоже под согласием
    setVoiceSubmitError(null);
    clearVoiceError();
    if (!isVoiceRecording) {
      await startVoiceRecording();
      return;
    }
    const blob = await stopVoiceRecording();
    if (!blob) return;
    setIsVoiceTranscribing(true);
    try {
      const { text } = await aiSupportApi.speechToText(blob);
      const trim = text?.trim();
      if (trim) {
        await handleSendMessage(trim);
      }
    } catch {
      setVoiceSubmitError(t("widget.voice.error"));
    } finally {
      setIsVoiceTranscribing(false);
    }
  }, [
    isVoiceTranscribing,
    isTyping,
    isVoiceRecording,
    startVoiceRecording,
    stopVoiceRecording,
    clearVoiceError,
    handleSendMessage,
    hasAiConsent,
    t,
  ]);

  /**
   * Закрытие чата закрывает сессию на бэкенде (`closeSupport`), и `sessionId`
   * обнуляется. Показывать прежнюю переписку рядом с НОВОЙ сессией нельзя —
   * это два разных обращения, а не продолжение одного.
   */
  useEffect(() => {
    if (!open && sessionId == null) {
      setMessages([]);
      setIsConnectedToOperator(false);
    }
  }, [open, sessionId]);

  // Приветственное сообщение
  useEffect(() => {
    if (open && messages.length === 0) {
      const welcomeMessage: Message = {
        id: "welcome",
        content: t("widget.welcome"),
        sender: "ai",
        timestamp: new Date(),
        type: "text",
        // «Уверенности» у зашитого приветствия нет: показывать под ним
        // 100 % — выдавать статичную строку за измеренный ответ модели.
        metadata: { suggestedActions: ["create-event", "help-center"] },
      };

      setMessages([welcomeMessage]);
    }
  }, [open, messages.length, t]);

  /**
   * Текст, с которым чат открыли снаружи (быстрое действие мобильной
   * навигации). Прежде он уходил в сессию хука — то есть в ленту, которой на
   * экране нет, и человек своего же вопроса не видел.
   */
  useEffect(() => {
    if (!open || !initialMessage) return;
    onInitialMessageSent?.();
    void handleSendMessage(initialMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMessage]);

  /**
   * Эскалация, запрошенная лаунчером. Выполняем её здесь: сессия и лента —
   * тут, и человек обязан увидеть ответ в той переписке, которую ведёт.
   */
  useEffect(() => {
    if (!escalationRequested) return;
    onEscalationHandled?.();
    void handleEscalateToOperator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationRequested]);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Обработка действий из предложенных кнопок
  const handleSuggestedAction = useCallback(
    (action: string, event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Определяем текст для отправки на основе действия
      let messageText = "";
      switch (action) {
        case "create-event":
          messageText = t("quickHelp.createEvent");
          break;
        case "event-wizard":
          messageText = "Покажите мастер создания мероприятий";
          break;
        case "participant-help":
          messageText = t("quickHelp.manageParticipants");
          break;
        case "billing-help":
          messageText = t("quickHelp.paymentIssue");
          break;
        case "troubleshooting":
          messageText = t("quickHelp.technicalProblem");
          break;
        case "help-center":
          messageText = "Покажите справочный центр";
          break;
        case "escalate":
          handleEscalateToOperator();
          return;
        default:
          messageText = String(
            t(`widget.actions.${action}`, { defaultValue: action }),
          );
      }

      if (messageText) {
        handleSendMessage(messageText);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [handleSendMessage, t],
  );

  // Обработка быстрого ответа
  const handleQuickReply = useCallback(
    (reply: QuickReply, event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      handleSendMessage(reply.text);
    },
    [handleSendMessage],
  );

  /**
   * Эскалация к оператору.
   *
   * Прежний код ставил `isConnectedToOperator = true` ДО запроса, печатал
   * выдуманную очередь («в очереди: 3 человека, ожидание 2-3 минуты») и глотал
   * ошибку в пустом catch: при лежащем бэкенде пользователь видел шапку
   * «Оператор» и ждал ответа, которого никто не получал. Теперь состояние
   * меняется только по фактическому ответу, а при сбое показывается отказ с
   * адресом почты — каналом, который от AI-контура не зависит.
   */
  const handleEscalateToOperator = useCallback(async () => {
    const pushSystemMessage = (content: string) =>
      setMessages((prev) => [
        ...prev,
        {
          // Сообщений об эскалации подряд может быть два (подключаю → результат);
          // при общей миллисекунде голый Date.now() дал бы совпадающий React-key.
          id: `escalation_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          content,
          sender: "ai",
          timestamp: new Date(),
          type: "escalation",
          metadata: { escalationReason: "user_request" },
        },
      ]);

    pushSystemMessage(t("escalation.connecting"));

    try {
      const sid = await ensureSession();
      if (!sid) {
        pushSystemMessage(t("escalation.failed", { email: SUPPORT_EMAIL }));
        return;
      }

      const result = await aiSupportApi.escalateSession(sid, "user_request");
      setIsConnectedToOperator(true);
      // B5-6: срок ответа не показывается — бэкенд его не знает и больше не
      // выдумывает. Различаем ровно то, что известно: оператор назначен либо
      // обращение принято в очередь.
      pushSystemMessage(
        result.operatorName
          ? t("escalation.assigned", { name: result.operatorName })
          : t("escalation.queued"),
      );
    } catch (error) {
      logger.error("Support escalation failed:", error);
      pushSystemMessage(t("escalation.failed", { email: SUPPORT_EMAIL }));
    }
  }, [ensureSession, t]);

  // Копирование сообщения
  const handleCopyMessage = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
  }, []);

  // Рендер сообщения
  const renderMessage = useCallback(
    (message: Message) => {
      const isUser = message.sender === "user";
      const isAI = message.sender === "ai";
      const isOperator = message.sender === "operator";

      return (
        <Fade key={message.id} in timeout={300}>
          <Box
            sx={{
              display: "flex",
              justifyContent: isUser ? "flex-end" : "flex-start",
              mb: 2,
            }}
          >
            {!isUser && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1,
                  bgcolor: isAI ? "primary.main" : "success.main",
                }}
              >
                {isAI ? (
                  <AIIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
              </Avatar>
            )}

            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                maxWidth: "75%",
                bgcolor: isUser ? "primary.main" : "background.paper",
                color: isUser ? "primary.contrastText" : "text.primary",
                borderRadius: 2,
                position: "relative",
              }}
            >
              {/* Информация об операторе */}
              {isOperator && message.metadata?.operatorInfo && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {message.metadata.operatorInfo.name} -{" "}
                    {message.metadata.operatorInfo.role}
                  </Typography>
                </Box>
              )}

              {/* Содержимое сообщения */}
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {message.content}
              </Typography>

              {/* Уверенность AI */}
              {isAI && message.metadata?.confidence && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("widget.confidence")}:{" "}
                    {Math.round(message.metadata.confidence * 100)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={message.metadata.confidence * 100}
                    sx={{ mt: 0.5, height: 2 }}
                  />
                </Box>
              )}

              {/* Действия сообщения */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1, justifyContent: "flex-end" }}
              >
                <Typography variant="caption" color="text.secondary">
                  {format(message.timestamp, "HH:mm", {
                    locale: getDateFnsLocale(),
                  })}
                </Typography>

                {!isUser && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyMessage(message.content)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <ThumbUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setFeedbackFor(message.id)}
                    >
                      <ThumbDownIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>

              {/* Inline feedback reason chips */}
              {feedbackFor === message.id && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}
                >
                  {(
                    [
                      "irrelevant",
                      "too_generic",
                      "incorrect",
                      "missing_steps",
                      "needs_human",
                    ] as const
                  ).map((reason) => (
                    <Chip
                      key={reason}
                      label={t(`widget.feedback.${reason}`)}
                      size="small"
                      variant="outlined"
                      color="warning"
                      clickable
                      onClick={() =>
                        handleFeedbackSubmit(
                          message.id,
                          message.content,
                          reason,
                        )
                      }
                    />
                  ))}
                  <Chip
                    key="cancel"
                    label={t("widget.feedback.cancel")}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => setFeedbackFor(null)}
                  />
                </Stack>
              )}

              {/* Предложенные действия */}
              {message.metadata?.suggestedActions && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
                >
                  {message.metadata.suggestedActions.map((action) => (
                    <Chip
                      key={action}
                      label={String(
                        t(`widget.actions.${action}`, { defaultValue: action }),
                      )}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={(event) => handleSuggestedAction(action, event)}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>
        </Fade>
      );
    },
    [
      handleCopyMessage,
      handleSuggestedAction,
      handleFeedbackSubmit,
      feedbackFor,
      t,
    ],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          height: isMobile ? "100%" : "80vh",
          maxHeight: isMobile ? "100%" : "80vh",
        },
      }}
    >
      {/* Заголовок */}
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Badge
              color={isConnectedToOperator ? "success" : "primary"}
              variant="dot"
            >
              {isConnectedToOperator ? <PersonIcon /> : <AIIcon />}
            </Badge>
            <Typography variant="h6">
              {isConnectedToOperator
                ? t("widget.operatorTitle")
                : t("widget.title")}
            </Typography>
          </Stack>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Статус подключения */}
        {isConnectedToOperator && (
          <Typography variant="caption" color="success.main">
            {t("widget.connected")}
          </Typography>
        )}
      </DialogTitle>

      <Divider />

      {/* Табы */}
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("tabs.chat")} />
        <Tab label={t("tabs.quickHelp")} />
        <Tab label={t("tabs.contact")} />
      </Tabs>

      {/* Содержимое */}
      <DialogContent
        sx={{ p: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {currentTab === 0 && (
          <>
            {/* Сообщения */}
            <Box sx={{ flexGrow: 1, p: 2, overflowY: "auto" }}>
              {messages.map(renderMessage)}

              {/* Индикатор набора текста */}
              {isTyping && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                    >
                      <AIIcon fontSize="small" />
                    </Avatar>
                    <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" spacing={0.5}>
                        <CircularProgress size={8} />
                        <CircularProgress
                          size={8}
                          sx={{ animationDelay: "0.2s" }}
                        />
                        <CircularProgress
                          size={8}
                          sx={{ animationDelay: "0.4s" }}
                        />
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Быстрые ответы */}
            {messages.length <= 2 && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("widget.quickReplies")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", gap: 1 }}
                >
                  {quickReplies.slice(0, 6).map((reply) => (
                    <Chip
                      key={reply.id}
                      label={reply.text}
                      variant="outlined"
                      size="small"
                      clickable
                      icon={getIconComponent(reply.icon)}
                      onClick={(event) => handleQuickReply(reply, event)}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}

        {currentTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("tabs.quickHelp")}
            </Typography>
            <List>
              {quickReplies.map((reply) => (
                <ListItem
                  key={reply.id}
                  component="div"
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={(event) => handleQuickReply(reply, event)}
                >
                  <ListItemIcon>{getIconComponent(reply.icon)}</ListItemIcon>
                  <ListItemText primary={reply.text} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {currentTab === 2 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("contact.title")}
            </Typography>
            <Stack spacing={2}>
              {/* Телефонная поддержка пока недоступна — карточка «Телефон»
                  скрыта намеренно (нет телефонной линии). Вернуть, когда
                  появится телефонный канал. */}

              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <EmailIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2">
                        {t("contact.email")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {SUPPORT_EMAIL}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ScheduleIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2">
                        {t("contact.hours")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("contact.hoursValue")}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* Ввод сообщения (только для чата) */}
      {currentTab === 0 && (
        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          {(voiceHookError || voiceSubmitError) && (
            <Typography variant="caption" color="error" sx={{ mb: 1 }}>
              {voiceHookError || voiceSubmitError}
            </Typography>
          )}
          <AiConsentGate>
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", alignItems: "center" }}
            >
              <TextField
                ref={inputRef}
                fullWidth
                size="small"
                placeholder={t("widget.inputPlaceholder")}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !e.shiftKey && handleSendMessage()
                }
                multiline
                maxRows={3}
                disabled={isTyping || isVoiceTranscribing}
              />

              <Tooltip
                title={
                  isVoiceTranscribing
                    ? t("widget.voice.transcribing")
                    : isVoiceRecording
                      ? t("widget.voice.recordingTooltip")
                      : t("widget.voice.startTooltip")
                }
              >
                <span>
                  <IconButton
                    color={isVoiceRecording ? "error" : "default"}
                    onClick={() => void handleVoiceToggle()}
                    disabled={isTyping || isVoiceTranscribing}
                    aria-label="voice-input"
                  >
                    {isVoiceTranscribing ? (
                      <CircularProgress size={22} />
                    ) : isVoiceRecording ? (
                      <StopIcon />
                    ) : (
                      <MicIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              {/* Ворота ТЗ §1.1: оператор — только после трёх ответов AI.
                  Прежде эта кнопка не гейтилась вовсе, а гейт лаунчера был
                  заперт насмерть — правило не соблюдалось ни там, ни там. */}
              {!isConnectedToOperator && canRequestOperator && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEscalateToOperator}
                  startIcon={<PersonIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {t("widget.operatorButton")}
                </Button>
              )}

              <IconButton
                color="primary"
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isTyping || isVoiceTranscribing}
              >
                <SendIcon />
              </IconButton>
            </Stack>
          </AiConsentGate>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SupportWidget;
